import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { buildBrief } from "@/lib/brief";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { TaskDetailDialog, EventDetailDialog } from "@/components/EntityDialogs";
import { Sparkles, Copy, Share2, Calendar, CheckSquare, AlertTriangle, Clock, ChevronRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShareDialog } from "@/components/ShareDialog";
import { aiBrief } from "@/lib/ai";

const Stat = ({ label, value, tone }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
    <p className={`mt-1.5 font-heading text-2xl font-semibold ${tone}`}>{value}</p>
  </div>
);

export default function DailyBrief() {
  const navigate = useNavigate();
  const { currentUser, events, tasks, renewals, documents } = useFgp();
  const brief = useMemo(() => buildBrief({ currentUser, events, tasks, renewals, documents }), [currentUser, events, tasks, renewals, documents]);

  const [taskId, setTaskId] = useState(null);
  const [selEvent, setSelEvent] = useState(null);
  const [eventOpen, setEventOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const summary = aiSummary || brief.summary;
  const genAiSummary = async () => {
    setAiLoading(true);
    try {
      const text = await aiBrief({
        greeting: brief.greeting, date: brief.dateLabel,
        overdueTasks: brief.stats.overdueTasks, renewalsDue: brief.stats.dueRenewals,
        expiringDocs: brief.stats.expiringDocs, needsAttention: brief.stats.attention,
        agenda: brief.agenda.map((e) => `${e.time} ${e.title} (${e.person})`),
        priorities: brief.priorities.map((t) => t.title),
        blockers: brief.blockers.map((b) => b.title),
      });
      if (text) { setAiSummary(text); toast.success("AI brief generated"); }
    } catch (err) {
      const message = err.message || "";
      if (/temporarily busy|free AI limit|taking too long|unavailable/i.test(message)) {
        setAiSummary("");
        toast.info("AI is busy; showing your live workspace brief.");
      } else {
        toast.error(message || "AI request failed");
      }
    } finally {
      setAiLoading(false);
    }
  };
  const detailTask = tasks.find((t) => t.id === taskId) || null;

  const listHtml = (items, empty) => items.length
    ? `<ul style="margin:6px 0 18px;padding-left:18px;font-family:Arial,sans-serif;font-size:13px;line-height:1.7">${items.map((x) => `<li>${x}</li>`).join("")}</ul>`
    : `<p class="sub" style="margin:6px 0 18px">${empty}</p>`;
  const shareHtml = `
    <div class="totals">
      <div class="box"><div class="lbl">Needs attention</div><div class="val">${brief.stats.attention}</div></div>
      <div class="box"><div class="lbl">Overdue tasks</div><div class="val" style="color:#dc2626">${brief.stats.overdueTasks}</div></div>
      <div class="box"><div class="lbl">Renewals due</div><div class="val" style="color:#d97706">${brief.stats.dueRenewals}</div></div>
      <div class="box"><div class="lbl">Docs expiring</div><div class="val" style="color:#d97706">${brief.stats.expiringDocs}</div></div>
    </div>
    <div class="card"><span class="k">Summary</span><div class="v">${summary}</div></div>
    <h3 style="font-size:15px;margin:20px 0 0">Today &amp; upcoming</h3>
    ${listHtml(brief.agenda.map((e) => `${e.time} — <strong>${e.title}</strong> · ${new Date(e.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · ${e.person}`), "Nothing scheduled ahead.")}
    <h3 style="font-size:15px;margin:20px 0 0">Priorities</h3>
    ${listHtml(brief.priorities.map((t) => t.title), "No open priorities.")}
    <h3 style="font-size:15px;margin:20px 0 0">Blockers</h3>
    ${listHtml(brief.blockers.map((b) => `${b.title} <em style="color:#94a3b8">(${b.kind})</em>`), "No blockers — all clear.")}`;

  const copyBrief = () => {
    const lines = [
      `${brief.greeting} — ${brief.dateLabel}`, "",
      summary, "",
      "Today's agenda:",
      ...(brief.agenda.length ? brief.agenda.map((e) => `• ${e.time} — ${e.title} (${e.person})`) : ["• Nothing scheduled"]), "",
      "Priorities:",
      ...(brief.priorities.length ? brief.priorities.map((t) => `• ${t.title}`) : ["• No open priorities"]), "",
      "Blockers:",
      ...(brief.blockers.length ? brief.blockers.map((b) => `• ${b.title} (${b.kind})`) : ["• None"]),
    ];
    navigator.clipboard?.writeText(lines.join("\n")).then(() => toast.success("Brief copied to clipboard"), () => toast.error("Copy failed"));
  };

  return (
    <div>
      <PageHeader
        title="Daily Brief"
        subtitle="Your one-glance morning summary — generated live from today's workspace."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={genAiSummary} disabled={aiLoading} className="gap-2" data-testid="brief-ai">
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-neutral-600" />} AI summary
            </Button>
            <Button variant="outline" onClick={copyBrief} className="gap-2" data-testid="brief-copy"><Copy className="h-4 w-4" /> Copy</Button>
            <Button onClick={() => setShareOpen(true)} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="brief-share"><Share2 className="h-4 w-4" /> Share brief</Button>
          </div>
        }
      />

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-neutral-200 bg-gradient-to-br from-white to-neutral-50/60 shadow-sm p-6 sm:p-8 mb-6">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="h-9 w-9 rounded-lg bg-neutral-600 text-white flex items-center justify-center"><Sparkles className="h-4 w-4" /></div>
          <div>
            <p className="font-heading text-lg font-semibold text-slate-900">{brief.greeting}</p>
            <p className="text-xs text-slate-400">{brief.dateLabel}</p>
          </div>
        </div>
        <p className="text-slate-600 leading-relaxed max-w-3xl" data-testid="brief-page-summary">{summary}</p>
      </motion.div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Needs attention" value={brief.stats.attention} tone="text-slate-900" />
        <Stat label="Overdue tasks" value={brief.stats.overdueTasks} tone="text-rose-600" />
        <Stat label="Renewals due" value={brief.stats.dueRenewals} tone="text-amber-600" />
        <Stat label="Docs expiring" value={brief.stats.expiringDocs} tone="text-amber-600" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Agenda */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4"><Calendar className="h-4 w-4 text-neutral-600" /><h2 className="font-heading font-semibold text-slate-900">Today &amp; upcoming</h2></div>
          <div className="space-y-2">
            {brief.agenda.map((e) => (
              <button key={e.id} onClick={() => { setSelEvent(e); setEventOpen(true); }} data-testid={`brief-agenda-${e.id}`}
                className="w-full text-left flex items-center gap-3 rounded-lg p-2 hover:bg-slate-50 transition-colors">
                <span className="text-xs text-slate-400 w-14 flex-shrink-0"><Clock className="h-3 w-3 inline mr-0.5" />{e.time}</span>
                <span className="flex-1 min-w-0"><span className="block text-sm font-medium text-slate-800 truncate">{e.title}</span><span className="block text-xs text-slate-400">{new Date(e.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · {e.person}</span></span>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </button>
            ))}
            {brief.agenda.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Nothing scheduled ahead.</p>}
          </div>
        </div>

        {/* Priorities */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4"><CheckSquare className="h-4 w-4 text-neutral-600" /><h2 className="font-heading font-semibold text-slate-900">Priorities</h2></div>
          <div className="space-y-2">
            {brief.priorities.map((t) => (
              <button key={t.id} onClick={() => setTaskId(t.id)} data-testid={`brief-priority-${t.id}`}
                className="w-full text-left flex items-center gap-3 rounded-lg p-2 hover:bg-slate-50 transition-colors">
                <span className={`h-2 w-2 rounded-full flex-shrink-0 ${t.priority === "high" ? "bg-rose-500" : "bg-amber-400"}`} />
                <span className="flex-1 min-w-0 text-sm font-medium text-slate-800 truncate">{t.title}</span>
                <StatusBadge status={t.status} />
              </button>
            ))}
            {brief.priorities.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No open priorities. 🎉</p>}
          </div>
        </div>

        {/* Blockers */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="h-4 w-4 text-rose-500" /><h2 className="font-heading font-semibold text-slate-900">Blockers</h2></div>
          <div className="space-y-2">
            {brief.blockers.map((b) => (
              <div key={b.id} className="flex items-start gap-3 rounded-lg p-2 bg-rose-50/40 border border-rose-100" data-testid={`brief-blocker-${b.id}`}>
                <span className="h-6 w-6 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center flex-shrink-0"><AlertTriangle className="h-3 w-3" /></span>
                <div><p className="text-sm font-medium text-slate-800 leading-snug">{b.title}</p><p className="text-xs text-slate-400">{b.kind}</p></div>
              </div>
            ))}
            {brief.blockers.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No blockers — all clear.</p>}
          </div>
        </div>
      </div>

      <TaskDetailDialog open={taskId !== null} onOpenChange={(o) => { if (!o) setTaskId(null); }} task={detailTask} />
      <EventDetailDialog open={eventOpen} onOpenChange={setEventOpen} event={selEvent} />
      {shareOpen && <ShareDialog open={shareOpen} onOpenChange={setShareOpen} testidPrefix="brief-share"
        docTitle={`Daily Brief — ${brief.dateLabel}`} docSubtitle={brief.greeting} bodyHtml={shareHtml}
        shareUrl={`${window.location.origin}/app/brief`} />}
    </div>
  );
}
